/**
 * Abu Dhabi minimum in the cart (snippets/clinic-delivery-minimum.liquid).
 *
 * Reads the city chosen in Zapiet's pickup box (inside the cart actions that follow this element) and
 * the cart total the theme rendered. With Abu Dhabi
 * chosen and the total under the minimum, it shows the notice, marks the cart's actions so CSS hides
 * Zapiet's "eligible" line and date picker, and holds Check out. Everything is re-read when Zapiet
 * redraws its box (city chosen or reset), when the cart is re-rendered with a new total (lines,
 * quantities, discounts) and before any checkout attempt. Checkout still enforces the rule itself.
 */
const CITY_SELECT = '#deliveryGeoSearchField';
const ABU_DHABI = /abu\s*dhabi/i;

class ClinicDeliveryMinimum extends HTMLElement {
  static observedAttributes = ['data-total'];

  /** @type {MutationObserver | undefined} */
  #observer;
  #queued = false;

  connectedCallback() {
    // The cart's actions follow this element (snippets/cart-summary.liquid).
    const next = this.nextElementSibling;
    this.ctas = next?.matches('.cart__ctas') ? next : this.parentElement?.querySelector('.cart__ctas');
    if (!this.ctas) return;

    this.#observer = new MutationObserver(() => this.#schedule());
    // Zapiet redraws its box inside the actions; a cart redraw can also drop the attribute set below.
    this.#observer.observe(this.ctas, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled', 'data-clinic-below-minimum'],
    });
    this.ctas.addEventListener('change', this.#onChange, true);
    document.addEventListener('click', this.#onCheckout, true);
    document.addEventListener('submit', this.#onSubmit, true);
    this.update();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.ctas?.removeEventListener('change', this.#onChange, true);
    document.removeEventListener('click', this.#onCheckout, true);
    document.removeEventListener('submit', this.#onSubmit, true);
    this.ctas?.removeAttribute('data-clinic-below-minimum');
  }

  attributeChangedCallback() {
    this.#schedule();
  }

  get belowMinimum() {
    const select = /** @type {HTMLSelectElement | null} */ (this.ctas?.querySelector(CITY_SELECT));
    const minimum = Number(this.dataset.minimum);
    const total = Number(this.dataset.total);
    return !!select && ABU_DHABI.test(select.value) && minimum > 0 && total < minimum;
  }

  #schedule() {
    if (this.#queued) return;
    this.#queued = true;
    requestAnimationFrame(() => {
      this.#queued = false;
      this.update();
    });
  }

  #onChange = (/** @type {Event} */ event) => {
    if (event.target instanceof Element && event.target.matches(CITY_SELECT)) this.#schedule();
  };

  /** Checkout buttons in this cart summary, including ones tied to the cart form by id. */
  #isCheckout(/** @type {EventTarget | null} */ target) {
    if (!(target instanceof Element)) return false;
    const button = target.closest('[name="checkout"]');
    return !!button && !!this.ctas?.contains(button);
  }

  #onCheckout = (/** @type {Event} */ event) => {
    if (!this.#isCheckout(event.target)) return;
    this.update();
    if (!this.belowMinimum) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.querySelector('.clinic-delivery-minimum__line')?.focus?.();
  };

  #onSubmit = (/** @type {SubmitEvent} */ event) => {
    if (!this.#isCheckout(event.submitter)) return;
    this.update();
    if (!this.belowMinimum) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  update() {
    if (!this.ctas) return;
    const below = this.belowMinimum;

    if (below) {
      const remaining = (Number(this.dataset.minimum) - Number(this.dataset.total)) / 100;
      const text = Number.isInteger(remaining) ? String(remaining) : remaining.toFixed(2);
      const field = this.querySelector('[data-field="remaining"]');
      if (field && field.textContent !== text) field.textContent = text;
    }

    if (this.hidden === below) this.hidden = !below;
    if (this.ctas.hasAttribute('data-clinic-below-minimum') !== below) {
      this.ctas.toggleAttribute('data-clinic-below-minimum', below);
    }
    for (const button of this.ctas.querySelectorAll('[name="checkout"]')) {
      if (below) button.setAttribute('aria-disabled', 'true');
      else if (button.getAttribute('aria-disabled') === 'true') button.removeAttribute('aria-disabled');
    }
  }
}

if (!customElements.get('clinic-delivery-minimum')) customElements.define('clinic-delivery-minimum', ClinicDeliveryMinimum);
